// libraryScope — which kind tabs the Library offers. The tabs are a view of
// the query text (libraryQuery.ts: a tab writes `type:`/`status:`, the text
// selects the tab), so this module only decides membership.
//
// Archive (F86) is a lifecycle overlay, not a kind: the archived tab shows
// every archived row regardless of kind; every other tab hides them.

import type { QueryScope } from "./libraryQuery.js";
import type { SearchResult, SearchResultKind } from "./searchModel.js";

const isArchived = (result: SearchResult): boolean => result.archived === true;

const ARCHIVED_SCOPE: readonly QueryScope[] = ["archived"];

// The tabs the strip offers. "all" always; a kind or the archived tab only
// when it has something in it (progressive disclosure: no empty drawer), or
// while it is the active tab (so archiving the last row, or opening
// pre-filtered to an empty kind, never strands the author on a vanished tab).
// Membership ignores the free text, so tabs don't flicker away while typing.
export const scopesFor = (
    visibleKinds: readonly SearchResultKind[],
    pool: readonly SearchResult[],
    active: QueryScope | null,
): readonly QueryScope[] => [
    "all",
    ...visibleKinds.filter(
        (kind) =>
            kind === active ||
            pool.some((r) => r.kind === kind && !isArchived(r)),
    ),
    ...(active === "archived" || pool.some(isArchived) ? ARCHIVED_SCOPE : []),
];

// How many rows a scope's tab holds: the live (unarchived) rows of its kind,
// every live row for "all", the archived rows for "archived".
export const scopeCount = (
    scope: QueryScope,
    pool: readonly SearchResult[],
): number =>
    pool.filter((r) =>
        scope === "archived"
            ? isArchived(r)
            : !isArchived(r) && (scope === "all" || r.kind === scope),
    ).length;
